import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { makeStyles, ButtonBase, Paper, Collapse, Tabs, Tab } from "@material-ui/core";
import {
  TuneOutlined,
  SettingsInputAntenna,
  Code,
  AccountTreeOutlined,
  ForumOutlined,
  PeopleOutline,
  ExpandMore,
  ArrowForward,
} from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTokens } from "../../styles/tokens";
import { PageHeader } from "../ui";
import { FilterBar, SearchField } from "../FormFields/panel";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    // Abas do tema (sublinhado na cor da marca) sobre um filete, como as `crd-tabs` do painel.
    nav: {
      flexShrink: 0,
      margin: "-6px 0 18px",
      borderBottom: `1px solid ${t.line}`,
      "& .MuiTab-root, & .MuiTab-labelIcon": { minWidth: 0, minHeight: 44, padding: "10px 13px", marginRight: 4 },
      "& .MuiTab-wrapper": { flexDirection: "row", gap: 8, whiteSpace: "nowrap" },
      "& .MuiTab-labelIcon .MuiTab-wrapper > *:first-child": { fontSize: 18, margin: 0 },
    },
    metrics: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 260px))",
      gap: 12,
      marginBottom: 16,
    },
    metric: {
      minWidth: 0,
      padding: "14px 16px",
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
      "& span": {
        display: "block",
        fontSize: 12.5,
        lineHeight: 1.4,
        color: t.textSecondary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& strong": {
        display: "block",
        marginTop: 4,
        fontSize: 22,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
        color: t.text,
      },
    },
    group: {
      overflow: "hidden",
      marginBottom: 12,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
    },
    groupButton: {
      width: "100%",
      justifyContent: "flex-start",
      textAlign: "left",
      padding: "14px 18px",
      gap: 14,
      color: t.textSecondary,
      transition: `background-color ${t.motion.fast}`,
      "&:hover": { backgroundColor: t.muted },
      "&:focus-visible": { outline: `2px solid ${t.brand}`, outlineOffset: -2 },
    },
    groupButtonOpen: { backgroundColor: t.muted, borderBottom: `1px solid ${t.line}` },
    icon: {
      flexShrink: 0,
      width: 36,
      height: 36,
      borderRadius: t.radius.sm,
      display: "grid",
      placeItems: "center",
      color: t.brand,
      backgroundColor: t.tint.soft,
      "& svg": { fontSize: 20 },
    },
    groupText: { flex: 1, minWidth: 0 },
    groupTitle: { fontSize: 14.5, fontWeight: 600, lineHeight: 1.4, color: t.text },
    groupDescription: { marginTop: 1, fontSize: 12.5, lineHeight: 1.5, color: t.textSecondary },
    chevron: { flexShrink: 0, transition: `transform ${t.motion.normal}` },
    chevronOpen: { transform: "rotate(180deg)" },
    groupBody: { padding: "6px 18px 16px", minWidth: 0 },
    cards: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: 12,
      margin: "4px 0 20px",
    },
    card: {
      display: "flex",
      alignItems: "flex-start",
      gap: 14,
      padding: 18,
      textAlign: "left",
      textDecoration: "none",
      color: t.textSecondary,
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
      transition: `border-color ${t.motion.normal}, box-shadow ${t.motion.normal}`,
      "&:hover": { borderColor: t.lineStrong, boxShadow: t.shadow.soft },
      "&:focus-visible": { outline: `2px solid ${t.brand}`, outlineOffset: 1 },
    },
    cardArrow: { flexShrink: 0, fontSize: 17, marginTop: 3, color: t.textMuted },
  };
});

/** Cabeçalho das telas administrativas — o `PageHeader` do painel (`eyebrow` ficou sem uso). */
export function AdminIntro({ title, description, actions }) {
  return <PageHeader title={title} subtitle={description} actions={actions} />;
}

/** Abas entre as telas de administração (só para perfil admin). */
export function AdminNav() {
  const c = useStyles();
  const { pathname } = useLocation();
  const { user } = useContext(AuthContext);
  if (user?.profile !== "admin") return null;
  const links = [
    ["/settings", "Configurações", <TuneOutlined />],
    ["/connections", "Canais", <SettingsInputAntenna />],
    ["/messages-api", "API", <Code />, user?.company?.plan?.useExternalApi],
    ["/queues", "Filas de atendimento", <ForumOutlined />],
    ["/flowbuilders", "Fluxos de conversa", <AccountTreeOutlined />, user?.showFlow === "enabled"],
    ["/users", "Equipe", <PeopleOutline />],
  ].filter((item) => item[3] !== false);
  const isActive = (to) => pathname === to || (to === "/flowbuilders" && pathname.startsWith("/flowbuilder/"));
  const current = links.find(([to]) => isActive(to));
  return (
    <Tabs
      component="nav"
      className={c.nav}
      value={current ? current[0] : false}
      variant="scrollable"
      scrollButtons="auto"
      indicatorColor="primary"
      textColor="primary"
      aria-label="Administração do sistema"
    >
      {links.map(([to, label, icon]) => (
        <Tab
          key={to}
          value={to}
          component={Link}
          to={to}
          icon={icon}
          label={label}
          aria-current={pathname === to ? "page" : undefined}
        />
      ))}
    </Tabs>
  );
}

export function AdminMetrics({ items }) {
  const c = useStyles();
  return (
    <div className={c.metrics}>
      {items.map((item) => (
        <Paper elevation={0} className={c.metric} key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </Paper>
      ))}
    </div>
  );
}

export function AdminSearch({ value, onChange, placeholder = "Buscar…", children }) {
  return (
    <FilterBar>
      <SearchField value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
      {children}
    </FilterBar>
  );
}

export function AdminCards({ items }) {
  const c = useStyles();
  return (
    <div className={c.cards}>
      {items.map((item) => (
        <ButtonBase
          key={item.title}
          component={item.to ? Link : "button"}
          to={item.to}
          onClick={item.onClick}
          className={c.card}
        >
          <span className={c.icon}>{item.icon || <TuneOutlined />}</span>
          <div className={c.groupText}>
            <div className={c.groupTitle}>{item.title}</div>
            <div className={c.groupDescription}>{item.description}</div>
          </div>
          <ArrowForward className={c.cardArrow} />
        </ButtonBase>
      ))}
    </div>
  );
}

export const normalizeSearch = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

// Only index labels and descriptions, never field values or credentials.
function contentLabels(children) {
  return React.Children.toArray(children)
    .map((child) => {
      if (typeof child === "string") return child;
      if (!React.isValidElement(child)) return "";
      return `${typeof child.props.label === "string" ? child.props.label : ""} ${
        typeof child.props.description === "string" ? child.props.description : ""
      } ${contentLabels(child.props.children)}`;
    })
    .join(" ");
}

/** Seção recolhível das Configurações: cartão do painel com ícone, título e descrição. */
export function SettingsSection({ id, title, description, icon, children, query = "", defaultOpen = false }) {
  const c = useStyles();
  const [open, setOpen] = useState(defaultOpen);
  const needle = normalizeSearch(query);
  const matches = !needle || normalizeSearch(`${title} ${description} ${contentLabels(children)}`).includes(needle);
  const expanded = Boolean(needle) || open;
  return (
    <Paper elevation={0} className={c.group} hidden={!matches}>
      <ButtonBase
        component="button"
        type="button"
        className={`${c.groupButton} ${expanded ? c.groupButtonOpen : ""}`}
        onClick={() => setOpen(!open)}
        aria-expanded={expanded}
        aria-controls={`settings-section-${id}`}
      >
        <span className={c.icon}>{icon || <TuneOutlined />}</span>
        <div className={c.groupText}>
          <div className={c.groupTitle}>{title}</div>
          <div className={c.groupDescription}>{description}</div>
        </div>
        <ExpandMore className={`${c.chevron} ${expanded ? c.chevronOpen : ""}`} />
      </ButtonBase>
      <Collapse in={expanded}>
        <div id={`settings-section-${id}`} className={c.groupBody}>
          {children}
        </div>
      </Collapse>
    </Paper>
  );
}
